import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Eyebrow from '@/ui/atoms/Eyebrow';
import { useFireOncePerUser } from '@/hooks/useFireOncePerUser';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { fireConfettiBurst, magicMomentStorageKey } from '@/lib/magic_moments';

// FirstWinBanner — P3/P4 wrapped-stat block at top of Report on the user's
// 1st council. Composes the magic-moment fire (report_ready_first_complete)
// with the visible "wrapped" copy.

interface FirstWinBannerProps {
  councilsRun: number;
  durationSeconds: number;
  ironKillCount: number;
  reportSectionCount: number;
  sessionId: string;
  v9Status?: string;
}

export function FirstWinBanner({
  councilsRun,
  durationSeconds,
  ironKillCount,
  reportSectionCount,
  sessionId,
  v9Status,
}: FirstWinBannerProps) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const { fired, fire } = useFireOncePerUser(
    'magic_moment_report_ready_first_complete_fired',
    magicMomentStorageKey('report_ready_first_complete'),
  );

  useEffect(() => {
    if (councilsRun > 1) return; // gate per spec: councils_run <= 1
    if (fired) return;
    fire({
      user_id: 'auth',
      timestamp: new Date().toISOString(),
      session_id: sessionId,
      v9_status: v9Status ?? 'normal',
      iron_kill_count: ironKillCount,
      report_section_count: reportSectionCount,
    });
    void fireConfettiBurst(ref.current, { reducedMotion });
  }, [
    councilsRun,
    fired,
    fire,
    sessionId,
    v9Status,
    ironKillCount,
    reportSectionCount,
    reducedMotion,
  ]);

  if (councilsRun > 1) return null;

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = String(durationSeconds % 60).padStart(2, '0');

  return (
    <div
      ref={ref}
      className="aha-unlock-reveal mb-10 px-8 py-6 border border-hairline bg-paper rounded-md"
      data-testid="first-win-banner"
    >
      <Eyebrow tone="gold" className="mb-3">
        {t('firstWin.wrappedStat.title')}
      </Eyebrow>
      <p className="font-serif text-2xl text-ink leading-snug mb-4">
        {t('firstWin.wrappedStat.metrics', {
          minutes: String(minutes).padStart(2, '0'),
          seconds,
          killCount: ironKillCount,
        })}
      </p>
      <p className="text-ink-2 leading-relaxed">{t('firstWin.wrappedStat.attribution')}</p>
    </div>
  );
}

export default FirstWinBanner;
